"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

export function ClaimPausedPanel({
  projectId,
  retryAfterSeconds,
  retryAt,
  onRetry,
  retrying = false,
  message,
  retryEnabled = true,
}: {
  projectId: string;
  retryAfterSeconds: number;
  retryAt?: number;
  onRetry: () => void;
  retrying?: boolean;
  message?: string | null;
  retryEnabled?: boolean;
}) {
  const [secondsRemaining, setSecondsRemaining] = useState(retryAfterSeconds);

  useEffect(() => {
    const deadline = retryAt ?? Date.now() + retryAfterSeconds * 1000;
    const update = () =>
      setSecondsRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));

    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfterSeconds, retryAt]);

  const waitLabel = secondsRemaining >= 60
    ? `${Math.ceil(secondsRemaining / 60)} min`
    : `${secondsRemaining}s`;

  return (
    <section className="rounded-lg border border-attn-line bg-attn-bg p-6">
      <h1 className="font-heading text-2xl font-extrabold text-navy">
        Your project is saved
      </h1>
      <p className="mt-3 text-sm leading-6 text-muted">
        Your uploaded PDF is saved with the project. Open it to continue; the
        Overview page shows the import status and any next step.
      </p>
      {message ? <p className="mt-3 text-sm font-semibold text-danger">{message}</p> : null}
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          href={`/projects/${projectId}`}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark"
        >
          Open saved project
        </Link>
        {retryEnabled ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying || secondsRemaining > 0}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-border px-5 py-3 text-sm font-bold text-navy disabled:opacity-50"
          >
            {retrying
              ? "Retrying claim..."
              : secondsRemaining > 0
                ? `Try again in ${waitLabel}`
                : "Try again"}
          </button>
        ) : null}
      </div>
    </section>
  );
}
